import React, { useCallback, useMemo } from 'react';
import classes from './style.module.scss';
import { PeriodicCheckPayload } from "types/index";
import useTranslation from '~/app/hooks/useTranslation';
import FileInput from '~/app/modules/form/File';
import { useStoreActions } from '~/store';
import { MAX_IMAGES_FOR_PERIODIC_CHECK } from '~/app/data/statuses/periodicCheck';
import Transition from '~/app/common/Transition';
import { buildImageMetadataEntry } from '~/app/helpers/imageMetadata';
import { normalizePeriodicCheckImages } from '~/periodicCheck/helpers';

export interface ImageUploadProps {
    item: PeriodicCheckPayload
    expanded?: boolean
    statusChange?: (status: PeriodicCheckPayload['status']) => void
}

export type ImageUploadComponent = React.FC<ImageUploadProps>

const ImageUpload: ImageUploadComponent = ({ item, expanded = false, statusChange }) => {

    const { t } = useTranslation("periodicCheck");
    const inputName = useMemo(() => `periodicCheckImages-${item.id}`, [item.id]);
    const existingImages = useMemo(() => normalizePeriodicCheckImages(item.images), [item.images]);
    const updateCheckData = useStoreActions(actions => actions.periodicCheck.updateCheckData);

    const onChange = useCallback(async ({ target: { value } }) => {
        const files = (value ?? []) as (globalThis.File | string)[];
        const im = files.length
            ? await Promise.all(files.map((file, index) =>
                file instanceof globalThis.File
                    ? buildImageMetadataEntry(file)
                    : Promise.resolve(item.im?.[index] ?? {})
            ))
            : [];
        const updatedCheck: PeriodicCheckPayload = {
            ...item,
            images: files,
            im,
        };
        updateCheckData(updatedCheck);
        if (updatedCheck.isImageRequired && !updatedCheck.images?.length && updatedCheck.status === 1) {
            statusChange?.(0);
        }
    }, [item, statusChange, updateCheckData]);

    return (
        <div className={`${classes['image-upload']}  ${expanded ? classes['expanded'] : classes['collapsed']}`}>
            <Transition
                in={expanded}
                timeout={500}
                classNames={{
                    enter: item.images?.length ? classes['image-uploaded-enter'] : classes['image-upload-enter'],
                    enterActive: item.images?.length ? classes['image-uploaded-enter-active'] : classes['image-upload-enter-active'],
                    enterDone: item.images?.length ? classes['image-uploaded-enter-done'] : classes['image-upload-enter-done'],
                    exit: item.images?.length ? classes['image-uploaded-exit'] : classes['image-upload-exit'],
                    exitActive: item.images?.length ? classes['image-uploaded-exit-active'] : classes['image-upload-exit-active'],
                    exitDone: item.images?.length ? classes['image-uploaded-exit-done'] : classes['image-upload-exit-done']
                }}
                mountOnEnter={true}
                unmountOnExit={false}
            >
                <FileInput
                    key={`${inputName}-${existingImages.length}`}
                    className={`${classes['input-container']}`}
                    placeholder={t('images.placeholder')}
                    name={inputName}
                    defaultValue={existingImages as string[]}
                    onChange={onChange}
                    maxFiles={MAX_IMAGES_FOR_PERIODIC_CHECK}
                    preview={true}
                    required={item.isImageRequired}
                    accept="image/*"
                />
            </Transition>
        </div>
    );
};

export default ImageUpload;
